import React from 'react';
import cn from 'classnames';
import { Map } from 'immutable';
import styles from './ResourcePermissions.module.scss';
import { DropdownMenuOption, ID } from '@repo/utils/types';
import { DropdownMenu } from '@repo/components/menus';

import { Toggle } from '@components/FormInputs';
import {
  labelsForPermissionsByResourceType,
  supportedPermissionsByResourceType,
  SharedResourceType,
} from '@repo/redux/reducers/data/shareStatuses';

type PermissionsMap<PermissionType> = Map<PermissionType, ID | null | true>;

function PermissionOption<P extends string>({
  sharedResourceType,
  permission,
  checked,
  onChange,
  disabled,
}: React.InputHTMLAttributes<HTMLInputElement> & {
  sharedResourceType: SharedResourceType;
  permission: P;
}) {
  const labelForPermission = labelsForPermissionsByResourceType[sharedResourceType] as {
    [permission in P]: string;
  };
  return (
    <Toggle
      className={cn(styles.option, checked && styles.checked, disabled && styles.disabled)}
      name={permission}
      checked={checked}
      onChange={onChange}
      clickableLabel={`Allow access to ${labelForPermission[permission]}`}
      disabled={disabled}
    />
  );
}

export default function ResourcePermissionsEditor<P>({
  sharedResourceType,
  globalPermissions = false,
  permissions,
  addPermission,
  removePermission,
  unshare,
  disabled,
}: {
  sharedResourceType: SharedResourceType;
  globalPermissions?: boolean;
  permissions: PermissionsMap<P>;
  addPermission: (permission: P) => void;
  removePermission: (permission: P) => void;
  unshare?: () => void;
  disabled?: boolean;
}) {
  const handleToggleClickFor = (permissionType: P) => () => {
    if (permissions.has(permissionType)) {
      removePermission(permissionType);
    } else {
      addPermission(permissionType);
    }
  };

  const supportedPermissions = supportedPermissionsByResourceType[sharedResourceType] as P[];

  let options: DropdownMenuOption[] = supportedPermissions.map((permission: P) => {
    const optionDisabled =
      !globalPermissions && permissions.has(permission) && !permissions.get(permission);
    return {
      title: (
        <PermissionOption
          sharedResourceType={sharedResourceType}
          permission={permission as string}
          checked={permissions.has(permission)}
          onChange={handleToggleClickFor(permission)}
          disabled={optionDisabled}
        />
      ),
      action: () => {},
      keepMenuOpenOnClick: true,
      className: cn(styles.accessOption, disabled && styles.disabled),
    };
  });

  if (!globalPermissions && unshare) {
    options = options.concat([
      {
        divider: true,
        disabled: true,
      },
      {
        title: 'Remove access',
        action: unshare,
      },
    ]);
  }

  return (
    <DropdownMenu
      triggerVariant="permissions"
      triggerProps={{ sharedResourceType, permissions }}
      expandFrom="top-right"
      options={options}
      triggerClassName={styles.editorTrigger}
      disabled={disabled}
    />
  );
}
